import { LINE_HEIGHT_RATIO, TEXT } from '../../../config/constants';
import type { TextStyle } from '../state/types';
import { parseTextLine, getFontSizeMultiplier, getFontWeight } from '../textEditor/TextFormatter';

export interface FontSpec {
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  fontFamily: string;
}

export interface TextLayoutResult {
  lines: string[];
  fontSize: number;
  lineHeight: number;
  font: string;
}

export interface TextBlockMetrics {
  lines: string[];
  lineHeight: number;
  width: number;
  height: number;
  font: string;
}

export const renderTextLines = (
  ctx: CanvasRenderingContext2D,
  options: {
    lines: string[];
    lineHeight: number;
    textX: number;
    startY: number;
    font?: string;
  }
): void => {
  if (options.font) {
    ctx.font = options.font;
  }
  options.lines.forEach((line, i) => {
    ctx.fillText(line, options.textX, options.startY + i * options.lineHeight);
  });
};

export const getFontString = (spec: FontSpec): string => {
  const fontStyleStr = spec.fontStyle === 'italic' ? 'italic ' : '';
  const fontWeightStr = spec.fontWeight === 'bold' ? 'bold ' : '';
  return `${fontStyleStr}${fontWeightStr}${spec.fontSize}px ${spec.fontFamily}`;
};

export type TextAnchor = 'start' | 'middle' | 'end';

export const resolveTextAlignPosition = (
  textAlign: TextStyle['textAlign'],
  left: number,
  center: number,
  right: number
): { textX: number; textAnchor: TextAnchor } => {
  if (textAlign === 'left') {
    return { textX: left, textAnchor: 'start' };
  }
  if (textAlign === 'right') {
    return { textX: right, textAnchor: 'end' };
  }
  return { textX: center, textAnchor: 'middle' };
};

export const getCenteredTextStartY = (
  centerY: number,
  lineHeight: number,
  lineCount: number
): number => centerY - ((lineCount - 1) * lineHeight) / 2;

export const getLineHeight = (fontSize: number, ratio = LINE_HEIGHT_RATIO): number =>
  fontSize * ratio;

export const measureTextBlock = (
  ctx: CanvasRenderingContext2D,
  text: string,
  spec: FontSpec,
  options: { padding?: number; lineHeightRatio?: number } = {}
): TextBlockMetrics => {
  const padding = options.padding ?? 0;
  const lineHeightRatio = options.lineHeightRatio ?? LINE_HEIGHT_RATIO;
  const font = getFontString(spec);
  ctx.font = font;
  const lines = text.split('\n');
  let maxWidth = 0;
  for (const line of lines) {
    const metrics = ctx.measureText(line);
    maxWidth = Math.max(maxWidth, metrics.width);
  }
  const lineHeight = getLineHeight(spec.fontSize, lineHeightRatio);
  const height = lines.length * lineHeight;
  return {
    lines,
    lineHeight,
    width: maxWidth + padding * 2,
    height: height + padding * 2,
    font,
  };
};

export const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string
): string[] => {
  ctx.font = font;
  const lines: string[] = [];
  const paragraphs = text.split('\n');

  for (const paragraph of paragraphs) {
    if (!paragraph) {
      lines.push('');
      continue;
    }

    const words = paragraph.split(' ');
    let currentLine = '';

    for (const word of words) {
      if (ctx.measureText(word).width > maxWidth) {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = '';
        }
        let remaining = word;
        while (remaining) {
          let chunk = '';
          for (let i = 0; i < remaining.length; i++) {
            const testChunk = remaining.slice(0, i + 1);
            if (ctx.measureText(testChunk).width > maxWidth && chunk) {
              break;
            }
            chunk = testChunk;
          }
          lines.push(chunk);
          remaining = remaining.slice(chunk.length);
        }
        continue;
      }

      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
  }

  return lines;
};

export const fitTextToBox = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxHeight: number,
  spec: FontSpec,
  minFontSize = TEXT.minFontSize,
  lineHeightRatio = LINE_HEIGHT_RATIO
): TextLayoutResult => {
  if (!text) {
    const font = getFontString(spec);
    return {
      lines: [],
      fontSize: spec.fontSize,
      lineHeight: getLineHeight(spec.fontSize, lineHeightRatio),
      font,
    };
  }

  let fontSize = spec.fontSize;
  let font = getFontString({ ...spec, fontSize });
  let lines = wrapText(ctx, text, maxWidth, font);
  let lineHeight = getLineHeight(fontSize, lineHeightRatio);
  let totalHeight = lines.length * lineHeight;

  while (totalHeight > maxHeight && fontSize > minFontSize) {
    fontSize -= 1;
    font = getFontString({ ...spec, fontSize });
    lines = wrapText(ctx, text, maxWidth, font);
    lineHeight = getLineHeight(fontSize, lineHeightRatio);
    totalHeight = lines.length * lineHeight;
  }

  return { lines, fontSize, lineHeight, font };
};

export const renderFormattedTextLines = (
  ctx: CanvasRenderingContext2D,
  options: {
    lines: string[];
    baseFontSize: number;
    baseLineHeight: number;
    textX: number;
    startY: number;
    fontWeight: string;
    fontStyle: string;
    fontFamily: string;
  }
): void => {
  // Enable smooth text rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  let currentY = options.startY;

  for (const line of options.lines) {
    const format = parseTextLine(line);

    // Calculate font size multiplier and weight
    const sizeMultiplier = getFontSizeMultiplier(format.type);
    const weight = getFontWeight(format.type, options.fontWeight as 'normal' | 'bold');

    const fontSize = options.baseFontSize * sizeMultiplier;
    const lineHeight = options.baseLineHeight * sizeMultiplier;

    // Set font with antialiasing
    ctx.font = `${options.fontStyle} ${weight} ${fontSize}px ${options.fontFamily}`;

    // Calculate indentation
    const indentOffset = format.indent * 12; // 12px per indent level
    const baseX = options.textX + indentOffset;

    // Render list markers and content with proper indentation
    if (format.type === 'unordered') {
      // Render bullet point
      ctx.fillText('•', baseX, currentY);
      // Render content with indent after bullet
      ctx.fillText(format.content, baseX + fontSize * 0.8, currentY);
    } else if (format.type === 'ordered' && format.listNumber !== undefined) {
      // Render number
      const numberText = `${format.listNumber}.`;
      ctx.fillText(numberText, baseX, currentY);
      // Render content with indent after number
      const numberWidth = ctx.measureText(numberText).width;
      ctx.fillText(format.content, baseX + numberWidth + fontSize * 0.4, currentY);
    } else {
      // Normal text or headers
      ctx.fillText(format.content, baseX, currentY);
    }

    currentY += lineHeight;
  }
};
